import React, { createContext, useContext, useRef } from 'react';
import { UseMutateAsyncFunction, useMutation } from 'react-query';

import { Config } from '@nw/shared/config';

import {
  RefreshAuthTokensRequestType,
  RefreshAuthTokensResponseType
} from '../services/NwAuthServices/APIService/types';
import {
  clearCollegePredictorAccessToken,
  clearCollegePredictorDomainTokens,
  clearCollegePredictorRefreshAccessToken,
  getCollegePredictorAccessToken,
  getCollegePredictorRefreshToken,
  setCollegePredictorAccessTokenToDomain,
  setCollegePredictorRefreshTokenToDomain
} from '../utils/NwCollegePredictorStorageUtils';
import { clearUserSessionAndReload } from '../utils/UserSessionUtils';

import { useGetClientAuthCodeAPI } from './IbUserAccounts/api';
import { useGetCollegePredictorClientAuthDetails } from './NwAuth/api';
import { useNwAuthRefreshTokenAPIService } from './NwAuth/useNwAuthRefreshTokenAPIService';

interface CollegePredictorRefreshAuthTokensProviderProps {
  children: React.ReactNode;
}

interface CollegePredictorRefreshAuthTokensContextTypes {
  refreshTokenApi: UseMutateAsyncFunction<
    RefreshAuthTokensResponseType,
    Error,
    RefreshAuthTokensRequestType
  >;
  getAccessToken: () => string;
  getRefreshToken: () => string;
}

export const CollegePredictorRefreshAuthTokensContext =
  createContext<CollegePredictorRefreshAuthTokensContextTypes | null>(null);

export const CollegePredictorRefreshAuthTokenProvider: React.FC<
  CollegePredictorRefreshAuthTokensProviderProps
> = (props) => {
  const refreshTokenPromise = useRef<Promise<RefreshAuthTokensResponseType>>();

  const { nwAuthRefreshTokenAPIService } = useNwAuthRefreshTokenAPIService();
  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
  const { getCollegePredictorClientAuthDetailsAPI } =
    useGetCollegePredictorClientAuthDetails();

  const { mutateAsync: refreshAuthTokensApi } = useMutation(
    async (requestObject: RefreshAuthTokensRequestType) => {
      refreshTokenPromise.current = new Promise<RefreshAuthTokensResponseType>(
        async (resolve, reject) => {
          try {
            if (requestObject.refresh_token) {
              const data =
                await nwAuthRefreshTokenAPIService.getRefreshToken(
                  requestObject
                );

              clearCollegePredictorAccessToken();
              clearCollegePredictorRefreshAccessToken();
              clearCollegePredictorDomainTokens();
              setCollegePredictorAccessTokenToDomain(data.access_token);
              setCollegePredictorRefreshTokenToDomain(data.refresh_token);

              resolve(data);
            } else {
              throw new Error(
                JSON.stringify({
                  response: 'Invalid refresh token',
                  http_status_code: 400,
                  res_status: 'REFRESH_TOKEN_EXPIRED'
                })
              );
            }
          } catch (e) {
            const errorResponse = JSON.parse(e.message);
            if (errorResponse.res_status === 'REFRESH_TOKEN_EXPIRED') {
              try {
                const authCodeResponse = await getClientAuthCodeAPI({
                  client_id: Config['NW_PREDICTOR_CLIENT_ID']
                });

                const clientAuthenticationDetails =
                  await getCollegePredictorClientAuthDetailsAPI({
                    client_id: Config['NW_PREDICTOR_CLIENT_ID'],
                    code: authCodeResponse.code
                  });

                setCollegePredictorAccessTokenToDomain(
                  clientAuthenticationDetails.access_token
                );
                setCollegePredictorRefreshTokenToDomain(
                  clientAuthenticationDetails.refresh_token
                );
                resolve(clientAuthenticationDetails);
              } catch (error) {
                clearUserSessionAndReload();
                reject(error);
              }
            } else {
              clearUserSessionAndReload();
              reject(new Error('Failed to refresh authentication token'));
            }
          }
        }
      );

      return refreshTokenPromise.current;
    }
  );

  return (
    <CollegePredictorRefreshAuthTokensContext.Provider
      value={{
        refreshTokenApi: refreshAuthTokensApi,
        getAccessToken: getCollegePredictorAccessToken,
        getRefreshToken: getCollegePredictorRefreshToken
      }}
    >
      {props.children}
    </CollegePredictorRefreshAuthTokensContext.Provider>
  );
};

export const useCollegePredictorRefreshAuthTokens =
  (): CollegePredictorRefreshAuthTokensContextTypes => {
    const collegePredictorRefreshAuthTokensContext = useContext(
      CollegePredictorRefreshAuthTokensContext
    );

    if (!collegePredictorRefreshAuthTokensContext)
      throw new Error(
        'Expected to be wrapped in an CollegePredictorRefreshAuthTokensProvider'
      );

    return collegePredictorRefreshAuthTokensContext;
  };
